'use client'

import type { ComponentProps, ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import {
  trackBookAppointment,
  trackFlightBridge,
  trackPhoneClick,
} from '@/lib/tracking'

type TrackEvent =
  | { event: 'book'; source: string }
  | { event: 'phone'; phone: string }
  | { event: 'flightbridge'; source?: string }

function fire(spec: TrackEvent) {
  if (spec.event === 'book') {
    trackBookAppointment(spec.source)
  } else if (spec.event === 'phone') {
    trackPhoneClick(spec.phone)
  } else {
    trackFlightBridge()
    if (spec.source) trackBookAppointment(spec.source)
  }
}

type ButtonProps = ComponentProps<typeof Button>

type TrackedButtonLinkProps = {
  href: string
  tracking: TrackEvent
  children: ReactNode
  external?: boolean
} & Omit<ButtonProps, 'asChild' | 'onClick'>

/**
 * A CTA button whose only client-side need is firing an analytics event.
 * Isolating it here lets the pages that use it render on the server.
 */
export function TrackedButtonLink({
  href,
  tracking,
  children,
  external,
  ...buttonProps
}: TrackedButtonLinkProps) {
  return (
    <Button asChild {...buttonProps}>
      <a
        href={href}
        onClick={() => fire(tracking)}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {children}
      </a>
    </Button>
  )
}

type TrackedLinkProps = {
  href: string
  tracking: TrackEvent
  children: ReactNode
  className?: string
}

/** A plain anchor that fires an analytics event on click. */
export function TrackedLink({ href, tracking, children, className }: TrackedLinkProps) {
  return (
    <a href={href} onClick={() => fire(tracking)} className={className}>
      {children}
    </a>
  )
}
